import GoogleCalendarIcon from '@forwardflow/assets/svg/GoogleCalendarIcon'
import { useGetCalendarList } from '@forwardflow/hooks/customHooks/useGetCalendarList'
import { useGetUserCalenderInfo } from '@forwardflow/hooks/useGetUserCalenderInfo'
import { CalendarCheckIcon, CalendarIcon, CheckCircle02Icon } from '@forwardflow/icons'
import { CalendarModal } from '@forwardflow/modals/CalendarModal'
import { UserService } from '@forwardflow/services/UserService'
import { useAppStore } from '@forwardflow/store'
import { useCalendarModal } from '@forwardflow/store/modal/calendarModal'
import Icon from '@forwardflow/ui/Icons'
import { message } from 'antd'
import { useMemo, useState } from 'react'
import ChannelConfig from '../ChannelConfig'
import FubInboxIntegration from '../FubInboxIntegration'

type CalendarProvider = 'google' | 'microsoft'

const providerContent: Record<
   CalendarProvider,
   { label: string; shortLabel: string; description: string }
> = {
   google: {
      label: 'Google Calendar',
      shortLabel: 'Google',
      description: 'Connect a Google Workspace or Gmail calendar.',
   },
   microsoft: {
      label: 'Outlook Calendar',
      shortLabel: 'Outlook',
      description: 'Connect a Microsoft 365 or Outlook calendar.',
   },
}

const ProviderIcon = ({ provider }: { provider: CalendarProvider }) => (
   <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white">
      {provider === 'microsoft' ? (
         <Icon name="microsoft-icon" className="w-6" />
      ) : (
         <GoogleCalendarIcon className="h-7 w-7" />
      )}
   </span>
)

const CalendarIntegration = () => {
   const { currentOrganization } = useAppStore()
   const {
      data: userCalendarInfo,
      mutate: mutateUserCalendarInfo,
      isLoading: isCalendarInfoLoading,
      error: calendarInfoError,
   } = useGetUserCalenderInfo(!!currentOrganization)
   const { openCalendarModal } = useCalendarModal()
   const [connectingProvider, setConnectingProvider] =
      useState<CalendarProvider | null>(null)

   const isCalendarConnected = !!userCalendarInfo?.email
   const {
      data: calendars,
      mutate: mutateCalendars,
      isLoading: calendarsLoading,
   } = useGetCalendarList(isCalendarConnected)

   const linkedCalendars = useMemo(
      () => userCalendarInfo?.preferredCalendars ?? [],
      [userCalendarInfo?.preferredCalendars]
   )

   const connectedProvider: CalendarProvider =
      userCalendarInfo?.provider === 'microsoft' ? 'microsoft' : 'google'
   const connectedProviderContent = providerContent[connectedProvider]

   const handleCalendarUpdate = async () => {
      await mutateCalendars()
      await mutateUserCalendarInfo()
   }

   const handleCalendarConnect = async (provider: CalendarProvider) => {
      if (connectingProvider) return

      setConnectingProvider(provider)
      try {
         const redirectUrl = encodeURIComponent(
            `${window.location.origin}/settings/integration`
         )
         const response =
            provider === 'microsoft'
               ? await UserService.getConnectUserWithOutlookCalendar(
                    redirectUrl,
                    currentOrganization || ''
                 )
               : await UserService.getConnectUserWithCalender(
                    redirectUrl,
                    currentOrganization || ''
                 )

         if (!response.redirectUrl) {
            throw new Error('Calendar provider did not return a redirect URL')
         }
         window.location.href = response.redirectUrl
      } catch (error) {
         console.error('Error connecting calendar:', error)
         message.error(
            `We couldn't start the ${providerContent[provider].shortLabel} connection. Please try again.`
         )
         setConnectingProvider(null)
      }
   }

   const isDefaultCalendar = (calendar: { id?: string; summary?: string }) =>
      calendar.id?.toLowerCase() === 'primary' ||
      calendar.summary?.toLowerCase() === 'primary' ||
      calendar.id?.toLowerCase() === userCalendarInfo?.email?.toLowerCase() ||
      calendar.summary?.toLowerCase() ===
         userCalendarInfo?.email?.toLowerCase()

   const renderCalendarCard = () => {
      if (isCalendarInfoLoading) {
         return (
            <div
               className="space-y-5 rounded-2xl border border-white/10 bg-white/[0.035] p-6"
               aria-label="Loading calendar connection"
               aria-busy="true"
            >
               <div className="h-5 w-40 animate-pulse rounded bg-white/10" />
               <div className="h-4 w-3/4 animate-pulse rounded bg-white/[0.07]" />
               <div className="grid gap-3 sm:grid-cols-2">
                  <div className="h-40 animate-pulse rounded-xl bg-white/[0.05]" />
                  <div className="h-40 animate-pulse rounded-xl bg-white/[0.05]" />
               </div>
            </div>
         )
      }

      if (!isCalendarConnected) {
         return (
            <section className="overflow-hidden rounded-2xl border border-white/10 bg-white/[0.035] shadow-[0_18px_50px_rgba(0,0,0,0.18)]">
               <div className="border-b border-white/10 p-5 sm:p-6">
                  <div className="flex items-start gap-4">
                     <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/20 text-primary-light">
                        <CalendarCheckIcon className="h-6 w-6" />
                     </span>
                     <div>
                        <h2 className="font-medium text-white">Connect your calendar</h2>
                        <p className="mt-1 max-w-2xl text-sm leading-6 text-white/60">
                           Let Forward Flow check your availability and add booked appointments to your calendar automatically.
                        </p>
                     </div>
                  </div>
                  <div className="mt-5 grid gap-2 text-xs text-white/65 sm:grid-cols-3">
                     {[
                        'Avoid double bookings',
                        'Keep availability in sync',
                        'Add appointments automatically',
                     ].map((benefit) => (
                        <span key={benefit} className="flex items-center gap-2">
                           <CheckCircle02Icon className="h-4 w-4 shrink-0 text-primary-light" />
                           {benefit}
                        </span>
                     ))}
                  </div>
               </div>

               {calendarInfoError && (
                  <div className="mx-5 mt-5 flex flex-col gap-3 rounded-xl border border-amber-200/20 bg-amber-200/[0.06] px-4 py-3 sm:mx-6 sm:flex-row sm:items-center sm:justify-between">
                     <p className="text-xs leading-5 text-white/65">
                        We couldn't confirm an existing calendar connection. You can still connect Google or Outlook below.
                     </p>
                     <button
                        type="button"
                        onClick={() => mutateUserCalendarInfo()}
                        className="shrink-0 text-left text-xs font-medium text-white underline decoration-white/30 underline-offset-4 hover:decoration-white sm:text-right"
                     >
                        Check again
                     </button>
                  </div>
               )}

               <div className="grid gap-3 p-5 sm:grid-cols-2 sm:p-6">
                  {(Object.keys(providerContent) as CalendarProvider[]).map(
                     (provider) => {
                        const content = providerContent[provider]
                        const isConnecting = connectingProvider === provider
                        return (
                           <article
                              key={provider}
                              className="flex min-w-0 flex-col rounded-xl border border-white/10 bg-black/10 p-4 transition hover:border-primary/45 hover:bg-primary/[0.06]"
                           >
                              <div className="flex items-center gap-3">
                                 <ProviderIcon provider={provider} />
                                 <div className="min-w-0">
                                    <h3 className="truncate text-sm font-medium text-white">
                                       {content.label}
                                    </h3>
                                    <p className="mt-1 text-xs leading-5 text-white/50">
                                       {content.description}
                                    </p>
                                 </div>
                              </div>
                              <button
                                 type="button"
                                 onClick={() => handleCalendarConnect(provider)}
                                 disabled={!!connectingProvider || !currentOrganization}
                                 aria-busy={isConnecting}
                                 className="mt-5 flex h-10 w-full items-center justify-center rounded-lg bg-white/10 px-4 text-sm font-medium text-white transition hover:bg-white/15 disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                 {isConnecting
                                    ? `Connecting to ${content.shortLabel}…`
                                    : `Connect ${content.shortLabel}`}
                              </button>
                           </article>
                        )
                     }
                  )}
               </div>
               <p className="border-t border-white/10 px-5 py-3 text-xs leading-5 text-white/45 sm:px-6">
                  You'll choose and approve an account on the provider's secure sign-in page. This does not change how you sign in to Forward Flow.
               </p>
            </section>
         )
      }

      return (
         <section className="overflow-hidden rounded-2xl border border-white/10 bg-white/[0.035] shadow-[0_18px_50px_rgba(0,0,0,0.18)]">
            <div className="flex flex-col gap-5 border-b border-white/10 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
               <div className="flex min-w-0 items-center gap-4">
                  <ProviderIcon provider={connectedProvider} />
                  <div className="min-w-0">
                     <div className="flex flex-wrap items-center gap-2">
                        <h2 className="font-medium text-white">
                           {connectedProviderContent.label}
                        </h2>
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-300/20 bg-emerald-300/10 px-2.5 py-1 text-[11px] font-medium text-emerald-200">
                           <span className="h-1.5 w-1.5 rounded-full bg-emerald-300" />
                           Connected
                        </span>
                     </div>
                     <p className="mt-1 truncate text-sm text-white/55">
                        {userCalendarInfo.email}
                     </p>
                  </div>
               </div>
               <button
                  type="button"
                  onClick={() => handleCalendarConnect(connectedProvider)}
                  disabled={!!connectingProvider}
                  aria-busy={!!connectingProvider}
                  className="h-10 rounded-lg border border-white/15 bg-white/[0.06] px-4 text-sm font-medium text-white transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50"
               >
                  {connectingProvider ? 'Opening provider…' : 'Change account'}
               </button>
            </div>

            <div className="p-5 sm:p-6">
               <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                     <h3 className="text-sm font-medium text-white">
                        Calendars used for availability
                     </h3>
                     <p className="mt-1 text-xs leading-5 text-white/50">
                        Forward Flow checks selected calendars for conflicts. New appointments are added to your primary calendar.
                     </p>
                  </div>
                  {!!calendars?.length && (
                     <button
                        type="button"
                        onClick={openCalendarModal}
                        className="h-9 shrink-0 rounded-lg bg-primary px-4 text-xs font-medium text-white transition hover:bg-primary-dark"
                     >
                        {linkedCalendars.length ? 'Manage calendars' : 'Choose calendars'}
                     </button>
                  )}
               </div>

               <div className="mt-5 space-y-2">
                  {calendarsLoading ? (
                     <>
                        <div className="h-14 animate-pulse rounded-xl bg-white/[0.05]" />
                        <div className="h-14 animate-pulse rounded-xl bg-white/[0.05]" />
                     </>
                  ) : linkedCalendars.length ? (
                     linkedCalendars.map((calendar) => {
                        const isDefault = isDefaultCalendar(calendar)
                        return (
                           <div
                              key={calendar.id}
                              className="flex items-center gap-3 rounded-xl border border-white/[0.08] bg-black/10 px-4 py-3"
                           >
                              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/[0.07] text-white/65">
                                 <CalendarIcon className="h-4 w-4" />
                              </span>
                              <div className="min-w-0 flex-1">
                                 <p className="truncate text-sm font-medium text-white/90">
                                    {isDefault ? 'Primary calendar' : calendar.summary}
                                 </p>
                                 {isDefault && (
                                    <p className="mt-0.5 truncate text-xs text-white/45">
                                       {userCalendarInfo.email}
                                    </p>
                                 )}
                              </div>
                              {isDefault && (
                                 <span className="rounded-full bg-white/[0.07] px-2.5 py-1 text-[11px] text-white/55">
                                    Appointment calendar
                                 </span>
                              )}
                           </div>
                        )
                     })
                  ) : (
                     <div className="rounded-xl border border-dashed border-white/15 bg-white/[0.025] px-4 py-5 text-center">
                        <p className="text-sm font-medium text-white/80">
                           Choose calendars to check for conflicts
                        </p>
                        <p className="mt-1 text-xs text-white/45">
                           Your primary calendar will remain the appointment calendar.
                        </p>
                     </div>
                  )}
               </div>
            </div>
         </section>
      )
   }

   return (
      <div className="flex h-full flex-1 flex-col overflow-hidden">
         <div className="flex h-20 shrink-0 items-center justify-between bg-white/5 px-5 sm:px-8">
            <div>
               <h1 className="text-lg font-medium text-white">Integrations</h1>
               <p className="mt-1 text-xs text-white/45">
                  Connect the services Forward Flow uses for your workflow.
               </p>
            </div>
         </div>
         <div className="min-h-0 flex-1 overflow-auto">
            <div className="max-w-3xl px-5 pb-8 pt-6 sm:px-8">
               {renderCalendarCard()}
               <ChannelConfig />
               <FubInboxIntegration />
            </div>
         </div>
         <CalendarModal
            onCalendarUpdate={handleCalendarUpdate}
            linkedCalendars={linkedCalendars}
            connectedEmail={userCalendarInfo?.email}
         />
      </div>
   )
}

export default CalendarIntegration
